import { createColumn, numberFormat } from "../../../../utils/helpers";
import { API_COUPON_SALES_EXPORT } from "../../../../utils/apis";

// Helper: Format Amount
const formatAmount = (amount: string | number) => {
  return `UGX ${Number(amount).toLocaleString("en-US")}`;
};

// Helper: Calculate Average per Coupon
const calculateAveragePerCoupon = (amount: number, coupons: number) => {
  if (!coupons || coupons === 0) return 0;
  return amount / coupons;
};

// Helper: Get Sales Performance Status
const getSalesPerformanceStatus = (coupons: number) => {
  if (coupons >= 25) return "Excellent";
  if (coupons >= 15) return "Good";
  if (coupons >= 5) return "Fair";
  return "Needs Improvement";
};

export const useCouponSalesReport = () => {
  const tableColumns = () => [
    createColumn("agent_name", true, "Agent Name"),
    createColumn("parking_yard", true, "Parking Yard"),
    createColumn("number_of_coupons_sold", false, "Coupons Sold"),
    createColumn("amount_paid", false, "Amount Paid"),
    createColumn("average_per_coupon", false, "Avg per Coupon"),
  ];

  const tableData = (data: any) => {
    // Handle both direct array and paginated response
    const results = data?.results || data;
    if (!results || !Array.isArray(results)) return [];

    // Filter out the total row for the table display
    const salesData = results.filter(
      (item: any) => item.parking_yard !== "TOTAL",
    );

    return salesData.map((item: any, index: number) => {
      const amount = parseFloat(item?.amount_paid ?? 0);
      const coupons = parseInt(item?.number_of_coupons_sold ?? 0);
      const averagePerCoupon = calculateAveragePerCoupon(amount, coupons);

      return {
        ...item,
        id: index,
        agent_name: item?.agent_name || "Unknown Agent",
        parking_yard: item?.parking_yard || "N/A",
        number_of_coupons_sold: coupons,
        amount_paid: numberFormat(amount),
        average_per_coupon: numberFormat(averagePerCoupon),
      };
    });
  };

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: { message: "No data available" },
          },
        ],
      };
    }

    const amount = parseFloat(item?.amount_paid ?? 0);
    const coupons = parseInt(item?.number_of_coupons_sold ?? 0);
    const averagePerCoupon = calculateAveragePerCoupon(amount, coupons);
    const performanceStatus = getSalesPerformanceStatus(coupons);

    return {
      sections: [
        {
          title: "Coupon Sales Details",
          fields: {
            agent_name: item?.agent_name || "Unknown Agent",
            parking_yard: item?.parking_yard || "N/A",
            number_of_coupons_sold: coupons,
            amount_paid: formatAmount(amount),
            average_per_coupon: formatAmount(averagePerCoupon),
            performance_status: performanceStatus,
          },
        },
        {
          title: "Sales Analysis",
          fields: {
            sales_efficiency: `${formatAmount(averagePerCoupon)} per coupon`,
            daily_target_status: coupons >= 10 ? "Met" : "Below Target",
            recommended_action:
              coupons >= 25
                ? "Maintain excellent performance"
                : coupons >= 15
                  ? "Good performance, aim for 25+ coupons"
                  : coupons >= 5
                    ? "Increase sales activities"
                    : "Requires immediate attention and support",
            total_revenue_contribution: formatAmount(amount),
          },
        },
      ],
    };
  };

  // Get totals from the API response
  const tableTotals = (data: any) => {
    // Handle both direct array and paginated response
    const results = data?.results || data;
    if (!results) return null;

    const totalRow = results?.find(
      (item: any) => item.parking_yard === "TOTAL",
    );

    return totalRow
      ? {
          number_of_coupons_sold: totalRow.total_number_of_coupons_sold || 0,
          amount_paid: formatAmount(totalRow.total_amount_paid || 0),
        }
      : null;
  };

  const activityFilter = "coupon_sales_report";

  const exportData = {
    api: API_COUPON_SALES_EXPORT,
    fileName: `coupon_sales_${new Date().toISOString().split("T")[0]}.xlsx`,
    params: {
      // Include any default export parameters
      include_metadata: true,
      format: "detailed",
    },
  };

  return {
    tableData,
    tableColumns,
    viewData,
    tableTotals,
    activityFilter,
    exportData,
  };
};
